import { Controller } from "@hotwired/stimulus";

import {
  budgetCategoryFilterEmptyMessage,
  budgetCategoryFilterFrom,
  budgetCategoryFilterStatus,
  budgetCategoryMatchesFilter,
} from "../utils/budget_category_filter";

export default class extends Controller<HTMLElement> {
  static targets = ["emptyState", "group", "option", "row", "status"];

  static values = {
    selected: { type: String, default: "all" },
  };

  declare readonly emptyStateTarget: HTMLElement;
  declare readonly groupTargets: HTMLElement[];
  declare readonly optionTargets: HTMLInputElement[];
  declare readonly rowTargets: HTMLElement[];
  declare readonly statusTarget: HTMLElement;

  declare readonly hasEmptyStateTarget: boolean;
  declare readonly hasStatusTarget: boolean;

  declare selectedValue: string;

  private renderQueued = false;

  connect(): void {
    this.scheduleRender();
  }

  select(event: Event): void {
    const option = event.currentTarget as HTMLInputElement;
    if (!option.checked) {
      return;
    }

    this.selectedValue = budgetCategoryFilterFrom(option.value);
  }

  selectedValueChanged(value: string): void {
    const selected = budgetCategoryFilterFrom(value);
    if (selected !== value) {
      this.selectedValue = selected;
      return;
    }

    this.scheduleRender();
  }

  rowTargetConnected(): void {
    this.scheduleRender();
  }

  rowTargetDisconnected(): void {
    this.scheduleRender();
  }

  private scheduleRender(): void {
    if (this.renderQueued) {
      return;
    }

    this.renderQueued = true;
    queueMicrotask(() => {
      this.renderQueued = false;
      if (!this.element.isConnected) {
        return;
      }

      this.render();
    });
  }

  private render(): void {
    const selected = budgetCategoryFilterFrom(this.selectedValue);
    this.optionTargets.forEach((option) => {
      option.checked = option.value === selected;
    });

    let visible = 0;
    this.rowTargets.forEach((row) => {
      const matches = budgetCategoryMatchesFilter(
        row.dataset.budgetCategoryFilterStates,
        selected,
      );
      row.hidden = !matches;
      if (matches) {
        visible += 1;
      }
    });

    this.groupTargets.forEach((group) => {
      group.hidden = !this.rowTargets.some(
        (row) => group.contains(row) && !row.hidden,
      );
    });

    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.hidden = visible !== 0;
      this.emptyStateTarget.textContent =
        budgetCategoryFilterEmptyMessage(selected);
    }
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = budgetCategoryFilterStatus(
        selected,
        visible,
        this.rowTargets.length,
      );
    }
  }
}
